import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "../Style/ContactUs.css";
import NavBar from "../Components/NavBar.js";
import Footer from "../Components/Footer.js";
import CandleRequestModal from "../Components/CandleRequestModal.js";
import BuildOrderModal from "../Components/BuildOrderModal.js";
import PurchaseModal from "../Components/PurchaseModal.js";
import TermsAndConditions from "../Components/TermsAndConditions.js";
import { api } from "../api/client.js";
import { resolveProductMediaUrl } from "../data/products.js";
import arrowRight from "../assets/Coolection/ArrowRight.svg";
import journeyOne from "../assets/Coolection/Journey1.png";
import journeyTwo from "../assets/Coolection/Journey2.png";
import journeyThree from "../assets/Coolection/Journey3.png";
import journeyFour from "../assets/Coolection/Journey4.png";
import sculpturalOne from "../assets/Coolection/SculpturalExperience/Sculptural1.png";
import sculpturalTwo from "../assets/Coolection/SculpturalExperience/Sculptural2.png";
import sculpturalThree from "../assets/Coolection/SculpturalExperience/Sculptural3.png";
import sculpturalFour from "../assets/Coolection/SculpturalExperience/Sculptural4.png";
import sculpturalFive from "../assets/Coolection/SculpturalExperience/Sculptural5.png";
import spacesOne from "../assets/Coolection/SpacesInspire/Spaces1.png";
import spacesTwo from "../assets/Coolection/SpacesInspire/Spaces2.png";
import spacesThree from "../assets/Coolection/SpacesInspire/Spaces3.png";
import riseBeautiful from "../assets/NewPhotos/RiseBeautifull.png";
import leftVectorRitual from "../assets/NewPhotos/LeftVectorRitual.svg";
import rightVectorRitual from "../assets/NewPhotos/RightVectorRitual.svg";

const PRICE_NOTE = "Tax included";

const JOURNEY_STEPS = [
  {
    icon: journeyOne,
    title: "Discover",
    text: "Share your vision and ideas with us.",
  },
  {
    icon: journeyTwo,
    title: "Consult",
    text: "We listen, guide, and curate possibilities.",
  },
  {
    icon: journeyThree,
    title: "Design",
    text: "We craft an experience that tells your story.",
  },
  {
    icon: journeyFour,
    title: "Celebrate",
    text: "Everything comes to life beautifully.",
  },
];

const SCULPTURES = [
  {
    image: sculpturalOne,
    title: "Balloon",
    text: "Represents hope and possibility.",
  },
  {
    image: sculpturalTwo,
    title: "Plaster Shell",
    text: "Light gently escapes, reminding us that beauty shines through life's imperfections.",
  },
  {
    image: sculpturalThree,
    title: "Hidden Structure",
    text: "Speaks to the quiet strength that supports us when no one else can see.",
  },
  {
    image: sculpturalFour,
    title: "Gentle Warmth",
    text: "Releases fragrance without flame, proving that transformation doesn't require intensity.",
  },
  {
    image: sculpturalFive,
    title: "Floral Foundation",
    text: "Celebrates growth through every season. A lasting reminder that beauty blooms beyond challenges.",
  },
];

const SPACES = [
  {
    image: spacesOne,
    title: "Hotels and lobby",
  },
  {
    image: spacesTwo,
    title: "Weddings and events",
  },
  {
    image: spacesThree,
    title: "Wellness and retreat",
  },
];

const formatProductPrice = (product) => {
  if (typeof product.price !== "number" || !product.currency) return "";

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: product.currency,
    maximumFractionDigits: 0,
  }).format(product.price);
};

const isCandleProduct = (product) => product.productType === "candle";

function ContactUs() {
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [productsError, setProductsError] = useState("");
  const [candleRequestOpen, setCandleRequestOpen] = useState(false);
  const [purchaseProduct, setPurchaseProduct] = useState(null);
  const [purchaseCandleIds, setPurchaseCandleIds] = useState([]);
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [termsRequiredOpen, setTermsRequiredOpen] = useState(false);
  const [buildOrderOpen, setBuildOrderOpen] = useState(false);
  const [buildTermsOpen, setBuildTermsOpen] = useState(false);
  const [buildTermsRequiredOpen, setBuildTermsRequiredOpen] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setProductsLoading(true);
    setProductsError("");

    api.products
      .list(
        { page: 1, limit: 100, featured: true },
        { signal: controller.signal },
      )
      .then((response) => {
        setProducts(
          (response.data || []).filter((product) => product.featured),
        );
        setProductsError("");
      })
      .catch((error) => {
        if (error.name !== "AbortError") {
          console.error(
            "Could not load collection products from the API",
            error,
          );
          setProductsError("Products could not be loaded.");
          setProducts([]);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setProductsLoading(false);
      });

    return () => controller.abort();
  }, []);

  const beginCandleOrder = (selectedCandles) => {
    if (selectedCandles.length === 0) {
      return "Please select at least one candle.";
    }

    const candleNames = selectedCandles.map((candle) => candle.fragranceNote);

    setPurchaseProduct({
      id:
        selectedCandles.length === 1
          ? selectedCandles[0].id
          : selectedCandles.map((candle) => candle.id).join("-"),
      name:
        selectedCandles.length === 1
          ? selectedCandles[0].fragranceNote
          : `Selected candles (${selectedCandles.length})`,
      mood:
        selectedCandles.length === 1
          ? selectedCandles[0].mood
          : candleNames.join(", "),
      color: selectedCandles[0].color,
      candles: selectedCandles,
      currency: "USD",
    });
    setPurchaseCandleIds(selectedCandles.map((candle) => candle.id));
    setCandleRequestOpen(false);
    setTermsOpen(true);
    return "";
  };

  const acceptTerms = () => {
    setTermsOpen(false);
    setPurchaseOpen(true);
  };

  const declineTerms = () => {
    setTermsOpen(false);
    setPurchaseOpen(false);
    setTermsRequiredOpen(true);
  };

  const acceptBuildTerms = () => {
    setBuildTermsOpen(false);
    setBuildOrderOpen(true);
  };

  const declineBuildTerms = () => {
    setBuildTermsOpen(false);
    setBuildOrderOpen(false);
    setBuildTermsRequiredOpen(true);
  };

  const balloonProducts = products.filter(
    (product) => !isCandleProduct(product),
  );
  const candleProducts = products.filter(isCandleProduct);
  const renderCollectionProduct = (product) => {
    const isOutOfStock = product.inStock === false;
    const collectionLabel = product.collectionLabel?.trim();
    const content = (
      <>
        {product.imageUrl && (
          <img
            src={resolveProductMediaUrl(product.imageUrl)}
            alt={product.alt || `${product.name || "Product"} collection`}
          />
        )}
        <span className="collection-product-info">
          <strong>{product.name || "Untitled"}</strong>
          {collectionLabel && <small>{collectionLabel}</small>}
          {formatProductPrice(product) && (
            <>
              <small>{formatProductPrice(product)}</small>
              <small className="product-price-note">{PRICE_NOTE}</small>
            </>
          )}
        </span>
        <span className={`collection-product-discover ${isOutOfStock}`}>
          {isOutOfStock ? "Out of stock" : "Discover"}
        </span>
      </>
    );

    // if (isOutOfStock) {
    //   return (
    //     <article
    //       className="collection-product-card is-out-of-stock"
    //       key={product.id}
    //       aria-label={`${product.name || "Product"} is out of stock`}
    //     >
    //       {content}
    //     </article>
    //   );
    // }

    return (
      <Link
        className="collection-product-card"
        to={`/products/${product.id}`}
        key={product.id}
      >
        {content}
      </Link>
    );
  };
  const renderProductTrack = (
    items,
    emptyMessage,
    showStatus = false,
    className = "collection-product-track",
  ) => (
    <div className={className}>
      {showStatus && productsLoading && (
        <p className="collection-product-message" role="status">
          Loading collection...
        </p>
      )}
      {showStatus && !productsLoading && productsError && (
        <p className="collection-product-message" role="alert">
          {productsError}
        </p>
      )}
      {!productsLoading && !productsError && items.map(renderCollectionProduct)}
      {!productsLoading && !productsError && items.length === 0 && (
        <p className="collection-product-message">{emptyMessage}</p>
      )}
    </div>
  );

  return (
    <div className="collection-page">
      <NavBar />

      <main className="collection-main">
        <section className="collection-section collection-journey">
          <h1 className="collection-title">Our journey together</h1>
          <div className="collection-journey-track">
            {JOURNEY_STEPS.map((step, index) => (
              <React.Fragment key={step.title}>
                <article className="collection-journey-step">
                  <img src={step.icon} alt="" aria-hidden="true" />
                  <h2>{step.title}</h2>
                  <p>{step.text}</p>
                </article>
                {index < JOURNEY_STEPS.length - 1 && (
                  <img
                    className="collection-journey-arrow"
                    src={arrowRight}
                    alt=""
                    aria-hidden="true"
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </section>

        <section className="collection-section collection-products">
          <h2 className="collection-title">Our collection</h2>
          {renderProductTrack(
            balloonProducts,
            "No balloon products yet.",
            true,
          )}
          {candleProducts.length > 0 &&
            renderProductTrack(
              candleProducts,
              "No candle products yet.",
              false,
              "collection-product-track collection-product-track-candles",
            )}
          <div className="collection-candle-action">
            <button
              type="button"
              className="collection-candle-button"
              onClick={() => setCandleRequestOpen(true)}
            >
              Request Candles
            </button>
            <button
              type="button"
              className="collection-candle-button"
              onClick={() => setBuildTermsOpen(true)}
            >
              Build your order
            </button>
          </div>
        </section>

        <section className="collection-section collection-sculptural">
          <h2 className="collection-title">Sculptural experience</h2>
          <div className="collection-sculpture-track">
            {SCULPTURES.map((item) => (
              <article className="collection-sculpture-card" key={item.title}>
                <div className="collection-sculpture-image">
                  <img src={item.image} alt="" aria-hidden="true" />
                </div>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="collection-section collection-spaces">
          <div className="collection-divider-title">
            <img src={leftVectorRitual} alt="" aria-hidden="true" />
            <h2>Created for spaces that inspire</h2>
            <img src={rightVectorRitual} alt="" aria-hidden="true" />
          </div>
          <div className="collection-spaces-track">
            {SPACES.map((space) => (
              <article className="collection-space-card" key={space.title}>
                <img src={space.image} alt={space.title} />
                <p>{space.title}</p>
              </article>
            ))}
          </div>
        </section>

        <section
          className="collection-signoff"
          aria-labelledby="collection-signoff-title"
        >
          <img src={riseBeautiful} alt="" aria-hidden="true" />
          <h2 id="collection-signoff-title">
            Handcrafted to elevate spaces.
            <br />
            Created to uplift the people within them.
          </h2>
          <p className="collection-signoff-script">Stay inspired</p>
          <p className="collection-signoff-note">
            Stories, more releases, gentle reminders to elevate your everyday..
          </p>
        </section>
      </main>

      <Footer />
      {candleRequestOpen && (
        <CandleRequestModal
          onClose={() => setCandleRequestOpen(false)}
          onOrder={beginCandleOrder}
        />
      )}

      <TermsAndConditions
        isOpen={termsOpen}
        onClose={declineTerms}
        showActions
        acceptText="Accept"
        denyText="Decline"
        onAccept={acceptTerms}
        onDeny={declineTerms}
      />

      <TermsAndConditions
        isOpen={termsRequiredOpen}
        title="Terms & Conditions Required"
        onClose={() => setTermsRequiredOpen(false)}
        closeText="OK"
        showCloseAction
        showHeaderClose={false}
        closeOnBackdrop={false}
      >
        <p>
          We cannot process or sell our products unless you accept the Terms
          &amp; Conditions governing the use of our services.
        </p>
      </TermsAndConditions>

      <TermsAndConditions
        isOpen={buildTermsOpen}
        onClose={declineBuildTerms}
        showActions
        acceptText="Accept"
        denyText="Decline"
        onAccept={acceptBuildTerms}
        onDeny={declineBuildTerms}
      />

      <TermsAndConditions
        isOpen={buildTermsRequiredOpen}
        title="Terms & Conditions Required"
        onClose={() => setBuildTermsRequiredOpen(false)}
        closeText="OK"
        showCloseAction
        showHeaderClose={false}
        closeOnBackdrop={false}
      >
        <p>
          We cannot process or sell our products unless you accept the Terms
          &amp; Conditions governing the use of our services.
        </p>
      </TermsAndConditions>

      {purchaseOpen && purchaseProduct && (
        <PurchaseModal
          product={purchaseProduct}
          itemType="candle"
          candleIds={purchaseCandleIds}
          returnPath="/contact-us"
          onClose={() => setPurchaseOpen(false)}
        />
      )}

      {buildOrderOpen && (
        <BuildOrderModal
          returnPath="/contact-us"
          onClose={() => setBuildOrderOpen(false)}
        />
      )}
    </div>
  );
}

export default ContactUs;
